import Notification from '@rio-cloud/rio-uikit/Notification';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import type { TourDocumentArchiveJobStatus } from '#api/tourApi.slice.ts';
import {
    useDownloadTourDocumentArchiveMutation,
    useGetTourDocumentArchiveJobQuery,
    useLazyGetTourDocumentArchiveJobQuery,
    useStartTourDocumentArchiveJobMutation,
} from '#api/tourApi.slice.ts';
import type { Tour } from '#domain/Tour.ts';

const pollingInterval = 2000;
const maxPollingAttempts = 150;

class ArchiveJobFailedError extends Error {}
class ArchiveJobTimeoutError extends Error {}

const delay = (duration: number) => new Promise(resolve => window.setTimeout(resolve, duration));
const isCompleted = (job: TourDocumentArchiveJobStatus) =>
    job.status === 'COMPLETED' || job.status === 'COMPLETED_PARTIAL';
const isRunning = (job?: TourDocumentArchiveJobStatus) => job?.status === 'RUNNING';
const getFailureMessageId = (error: unknown) => {
    if (error instanceof ArchiveJobTimeoutError) {
        return 'intl-msg:ordercom-cloud-frontend.tours.archive.timeout';
    }
    return 'intl-msg:ordercom-cloud-frontend.tours.archive.failed';
};

export const useDownloadTourDocumentArchive = (tour?: Tour) => {
    const intl = useIntl();
    const [pollCurrentArchiveJob, setPollCurrentArchiveJob] = useState(false);
    const [isDownloadArchiveInProgress, setIsDownloadArchiveInProgress] = useState(false);
    const downloadArchiveInProgressRef = useRef(false);
    const shouldQueryArchiveJob = tour?.status === 'finished';
    const { data: currentArchiveJob, isFetching: isFetchingCurrentArchiveJob } = useGetTourDocumentArchiveJobQuery(
        tour?.id ?? '',
        {
            skip: !shouldQueryArchiveJob,
            pollingInterval: pollCurrentArchiveJob ? pollingInterval : 0,
        }
    );
    const [startArchiveJob, { isLoading: isStarting }] = useStartTourDocumentArchiveJobMutation();
    const [getArchiveJob, { isFetching: isPolling }] = useLazyGetTourDocumentArchiveJobQuery();
    const [downloadArchive, { isLoading }] = useDownloadTourDocumentArchiveMutation();

    useEffect(() => {
        setPollCurrentArchiveJob(isRunning(currentArchiveJob));
    }, [currentArchiveJob]);

    const downloadTourDocumentArchive = async (tour: Tour) => {
        if (downloadArchiveInProgressRef.current) {
            return;
        }

        downloadArchiveInProgressRef.current = true;
        setIsDownloadArchiveInProgress(true);
        try {
            let job = await startArchiveJob(tour.id).unwrap();

            for (
                let attempt = 0;
                !isCompleted(job) && job.status !== 'FAILED' && attempt < maxPollingAttempts;
                attempt++
            ) {
                await delay(pollingInterval);
                job = await getArchiveJob(tour.id, false).unwrap();
            }

            if (job.status === 'FAILED') {
                throw new ArchiveJobFailedError(job.errorMessage ?? 'Tour document archive job failed');
            }

            if (!isCompleted(job)) {
                throw new ArchiveJobTimeoutError('Tour document archive job timed out');
            }

            const archive = await downloadArchive(tour.id).unwrap();

            if (archive.isPartial || job.partial) {
                Notification.warning(
                    intl.formatMessage({
                        id: 'intl-msg:ordercom-cloud-frontend.tours.archive.partial',
                    })
                );
            }
        } catch (error) {
            console.error('Failed to download tour document archive: ', error);
            Notification.error(
                intl.formatMessage({
                    id: getFailureMessageId(error),
                })
            );
        } finally {
            downloadArchiveInProgressRef.current = false;
            setIsDownloadArchiveInProgress(false);
        }
    };

    return {
        downloadTourDocumentArchive,
        isDownloading:
            isDownloadArchiveInProgress ||
            isRunning(currentArchiveJob) ||
            isStarting ||
            isPolling ||
            isLoading ||
            isFetchingCurrentArchiveJob,
    };
};
